import type { AttributionPayload } from '@markku/types/acquisition'
import { captureAcquisition, isNewTouch } from './capture'

const STORAGE_KEY = 'mk_attr'
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 180 // 180 days

function readCookie(): AttributionPayload | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${STORAGE_KEY}=`))
  if (!match) return null
  try {
    return JSON.parse(decodeURIComponent(match.slice(STORAGE_KEY.length + 1)))
  } catch {
    return null
  }
}

// Same-tab fallback for cookies-disabled browsers.
function readSessionStorage(): AttributionPayload | null {
  if (typeof sessionStorage === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw)
  } catch {
    return null
  }
}

function writeSessionStorage(payload: AttributionPayload): void {
  if (typeof sessionStorage === 'undefined') return
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  } catch {
    // sessionStorage disabled — cookie still persists attribution
  }
}

// Share the cookie across subdomains of the root site by setting Domain to the
// registrable domain. Returns null for localhost / bare IPs / single-label
// hosts where a Domain attribute is not valid. Per RFC 6265 the leading dot
// is deprecated — without it, the cookie already applies to all subdomains.
function getCookieDomain(): string | null {
  const host = window.location.hostname
  if (host === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(host)) return null
  const parts = host.split('.')
  if (parts.length < 2) return null
  // Last two labels = registrable domain for all TLDs we use (.fi, .com).
  // Does not handle multi-label TLDs like .co.uk, which we don't ship on.
  return parts.slice(-2).join('.')
}

function writeCookie(payload: AttributionPayload): void {
  if (typeof document === 'undefined') return
  const value = encodeURIComponent(JSON.stringify(payload))
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  const domain = getCookieDomain()
  const domainAttr = domain ? `; Domain=${domain}` : ''
  document.cookie = `${STORAGE_KEY}=${value}; Max-Age=${COOKIE_MAX_AGE_SECONDS}; Path=/${domainAttr}; SameSite=Lax${secure}`
}

export function getAttribution(): AttributionPayload | null {
  return readCookie() ?? readSessionStorage()
}

// Call on every page load. Updates lastTouch when a new identifiable channel
// impression is detected; initializes the cookie on the very first visit.
// Returns the current state either way so callers can send it along with
// form submissions.
export function syncAttributionCookie(): AttributionPayload {
  const current = captureAcquisition()
  const existing = readCookie() ?? readSessionStorage()

  if (!existing) {
    const payload: AttributionPayload = {
      firstTouch: current,
      lastTouch: current,
    }
    writeCookie(payload)
    writeSessionStorage(payload)
    return payload
  }

  if (!isNewTouch(current)) return existing

  const payload: AttributionPayload = {
    firstTouch: existing.firstTouch,
    lastTouch: current,
  }
  writeCookie(payload)
  writeSessionStorage(payload)
  return payload
}
